import type { Monaco } from "@monaco-editor/react";
import { SUGGEST_CONTROLLER_ACTIVE_STATE } from "../ChatInput.constants";
import type { MonacoEditor } from "../ChatInput.types";

/**
 * Keys that are always allowed through (plain, no modifier required).
 * Everything else with a Ctrl/Cmd/Alt modifier is blocked.
 */
const ALWAYS_ALLOWED_KEYS = new Set([
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Enter",
  "Escape",
  "Tab",
]);

/**
 * Whitelist of Ctrl/Cmd shortcuts we allow.
 * Format: "key" (lowercase) — the modifier (Ctrl/Cmd) is implicit.
 * Shift variants (e.g. Cmd+Shift+Z for redo) are allowed automatically
 * when the base key is whitelisted.
 */
const ALLOWED_CMD_KEYS = new Set([
  "a", // select all
  "c", // copy
  "v", // paste
  "x", // cut
  "z", // undo / redo (with shift)
]);

/**
 * Whitelist approach: intercept keydown at the DOM capture phase and
 * block any modifier-based shortcut that isn't explicitly allowed.
 * This prevents Monaco's internal handlers (find widget, command palette, etc.)
 * from ever seeing the event.
 */
export function disableMonacoCommands(editor: MonacoEditor, _monaco: Monaco) {
  const container = editor.getContainerDomNode();

  container.addEventListener(
    "keydown",
    (e: KeyboardEvent) => {
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey;
      if (!hasModifier) return; // plain keystrokes are fine

      // Allow whitelisted Ctrl/Cmd keys (with or without Shift)
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (ALLOWED_CMD_KEYS.has(e.key.toLowerCase())) return;
      }

      // Allow navigation keys even with modifiers (e.g. Shift+ArrowLeft for selection)
      if (ALWAYS_ALLOWED_KEYS.has(e.key)) return;

      // Block everything else before Monaco sees it
      e.preventDefault();
      e.stopPropagation();
    },
    { capture: true }
  );
}

export interface KeyboardHandlerOptions {
  onSubmit: () => void;
  onCancel?: () => void;
  updateHeight: () => void;
}

export function setupEditorKeyboardHandlers(
  editor: MonacoEditor,
  monaco: Monaco,
  options: KeyboardHandlerOptions
) {
  const { onSubmit, onCancel, updateHeight } = options;

  // Enter to send (without shift)
  // If suggestions are visible, accept suggestion instead of sending
  editor.addCommand(monaco.KeyCode.Enter, () => {
    const suggestController = editor.getContribution(
      "editor.contrib.suggestController"
    ) as { model?: { state?: number } } | null;
    // state 2 = suggestions are visible
    if (suggestController?.model?.state === SUGGEST_CONTROLLER_ACTIVE_STATE) {
      editor.trigger("keyboard", "acceptSelectedSuggestion", null);
    } else {
      onSubmit();
    }
  });

  // Shift+Enter for new line (default behavior, but explicit)
  editor.addCommand(monaco.KeyMod.Shift | monaco.KeyCode.Enter, () => {
    editor.trigger("keyboard", "type", { text: "\n" });
    updateHeight();
  });

  // Escape to cancel (if onCancel is provided)
  if (onCancel) {
    editor.addCommand(monaco.KeyCode.Escape, () => {
      onCancel();
    });
  }
}
